import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice, getSessionId } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image Gallery Component for accessories
function ImageGallery({ images, alt }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const imageList = images && images.length > 0 ? images : [];
  
  if (imageList.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-secondary">
        <span className="text-muted-foreground text-sm">Pas d'image</span>
      </div>
    );
  }

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % imageList.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
  };

  return (
    <div className="relative w-full h-full">
      <img
        src={imageList[currentIndex]}
        alt={alt}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
      
      {imageList.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white border border-border flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white border border-border flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          
          {/* Dots indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {imageList.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-black' : 'bg-white/70'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AccessoiresPage() {
  const { addToCart } = useCart();
  const { language } = useLanguage();
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.post(`${API}/visitor/log`, {
      page: 'accessoires',
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer
    }).catch(() => {});

    // Fetch accessories from API
    axios.get(`${API}/accessories`)
      .then(res => {
        setAccessories(res.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const handleAddToCart = (accessory) => {
    const displayName = language === 'en' ? (accessory.name_en || accessory.name) : accessory.name;
    addToCart({
      product_id: accessory.id,
      product_name: displayName,
      product_type: 'accessory',
      config: null,
      quantity: 1,
      unit_price: accessory.price,
      total_price: accessory.price
    });
    toast.success(language === 'fr' 
      ? `${accessory.name} ajouté au panier`
      : `${accessory.name_en || accessory.name} added to cart`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse font-mono text-sm">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="accessoires-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            {language === 'fr' ? 'Équipez votre vélo' : 'Equip your bike'}
          </span>
          <h1 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mt-2">
            {language === 'fr' ? 'Accessoires' : 'Accessories'}
          </h1>
        </motion.div>

        {accessories.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-mono text-muted-foreground">
              {language === 'fr' ? 'Aucun accessoire disponible' : 'No accessories available'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {accessories.map((accessory, index) => (
              <motion.div
                key={accessory.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-border group"
                data-testid={`accessory-card-${accessory.id}`}
              >
                <div className="aspect-square bg-secondary overflow-hidden">
                  <ImageGallery 
                    images={accessory.images} 
                    alt={language === 'en' ? (accessory.name_en || accessory.name) : accessory.name}
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-lg uppercase">
                    {language === 'en' ? (accessory.name_en || accessory.name) : accessory.name}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground mt-2">
                    {language === 'en' ? (accessory.description_en || accessory.description) : accessory.description}
                  </p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-mono font-bold text-xl">{formatPrice(accessory.price)}</span>
                    <Button
                      onClick={() => handleAddToCart(accessory)}
                      className="h-10 px-4 rounded-none font-mono uppercase tracking-widest text-xs"
                      data-testid={`add-accessory-${accessory.id}`}
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Ajouter' : 'Add'}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
